/**
 * LA TRÉSORERIE, CÔTÉ ÉCRAN — lot 100, 18/09/2026.
 *
 * Refait le plan (probable, prudent) à partir des flux rendus par
 * `GET /analyse/tresorerie`, par les fonctions de `@stafa/core`, et prépare
 * les tuiles, le tableau et le passé. Aucun dessin ici.
 */

import {
  centimes,
  niveau,
  planTresorerie,
  pointBas,
  semainesDe,
  semainesPassees,
  type FluxTresorerie,
  type MouvementRange,
  type Nature,
  type Niveau,
  type PhotoSemaine,
  type RegleNature,
  type ReglagesTresorerie,
  type SemaineBfr,
  type SemainePassee,
  type SemainePlan,
  type TvaDuMois,
} from '@stafa/core';
import type { ReglagesAnalyse } from './bfr.js';

export interface RegleLue extends RegleNature {
  readonly cree_par: string;
  readonly cree_le: string;
}

export interface TresorerieLue {
  readonly aujourdhui: string;
  readonly reglages: ReglagesAnalyse;
  readonly comptes: readonly {
    id: string;
    nom: string;
    solde: string | null;
    lu_le: string;
  }[];
  readonly solde: number;
  readonly flux: readonly FluxTresorerie[];
  readonly mouvements: readonly MouvementRange[];
  readonly photos: readonly PhotoSemaine[];
  readonly photo_prise: boolean;
  readonly regles: readonly RegleLue[];
  readonly affactures: readonly string[];
  readonly tva: readonly TvaDuMois[];
}

/** Semaines passées montrées : six, plus la semaine en cours. */
export const SEMAINES_PASSEES = 7;

export function reglagesDe(l: TresorerieLue): ReglagesTresorerie {
  return {
    retenue_factor: Math.round(Number(l.reglages.retenue_factor) * 100),
    commission_factor: Math.round(Number(l.reglages.commission_factor) * 100),
    delai_cession: l.reglages.delai_cession,
    minimum: centimes(l.reglages.minimum_tresorerie),
    decouvert: centimes(l.reglages.decouvert_autorise),
  };
}

export interface Tuiles {
  readonly solde: number;
  readonly comptesSansSolde: number;
  readonly basProbable: SemainePlan | null;
  readonly basPrudent: SemainePlan | null;
  readonly sousMinimumProbable: number;
  readonly sousMinimumPrudent: number;
  readonly aRevoir: readonly FluxTresorerie[];
}

export interface VueTresorerie {
  readonly semaines: readonly SemaineBfr[];
  readonly probable: readonly SemainePlan[];
  readonly prudent: readonly SemainePlan[];
  readonly reglages: ReglagesTresorerie;
  readonly tuiles: Tuiles;
  readonly passe: readonly SemainePassee[];
  readonly photos: ReadonlyMap<string, PhotoSemaine>;
}

export function vueTresorerie(l: TresorerieLue): VueTresorerie {
  const semaines = semainesDe(l.aujourdhui, 'avenir');
  const probable = planTresorerie(l.solde, l.flux, semaines, 'probable', l.aujourdhui);
  const prudent = planTresorerie(l.solde, l.flux, semaines, 'prudent', l.aujourdhui);
  const reglages = reglagesDe(l);
  const sous = (p: readonly SemainePlan[]) =>
    p.filter((x) => x.fin < reglages.minimum).length;
  return {
    semaines,
    probable,
    prudent,
    reglages,
    tuiles: {
      solde: l.solde,
      comptesSansSolde: l.comptes.filter((c) => c.solde === null).length,
      basProbable: pointBas(probable),
      basPrudent: pointBas(prudent),
      sousMinimumProbable: sous(probable),
      sousMinimumPrudent: sous(prudent),
      aRevoir: l.flux.filter((f) => f.a_revoir),
    },
    passe: semainesPassees(
      l.solde,
      l.mouvements,
      semainesDe(l.aujourdhui, 'passe').slice(-SEMAINES_PASSEES),
      l.aujourdhui,
    ),
    photos: new Map(l.photos.map((p) => [p.lundi, p])),
  };
}

export function niveauDe(v: number, r: ReglagesTresorerie): Niveau {
  return niveau(v, r);
}

export const NOM_NIVEAU: Readonly<Record<Niveau, string>> = {
  vert: 'au-dessus du minimum',
  orange: 'sous le minimum',
  rouge: 'au-delà du découvert',
};

/** Les flux d'un groupe, par nature, dans l'ordre des dates. */
export function parNature(
  flux: readonly FluxTresorerie[],
): { nature: Nature; flux: FluxTresorerie[]; total: number }[] {
  const m = new Map<Nature, FluxTresorerie[]>();
  for (const f of flux) m.set(f.nature, [...(m.get(f.nature) ?? []), f]);
  return [...m].map(([nature, liste]) => ({
    nature,
    flux: [...liste].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)),
    total: liste.reduce((s, f) => s + f.montant, 0),
  }));
}

/** « +1 250 € », « −3 400 € », « 0 € » — pour le tableau. */
export function eurosSignes(c: number, decimales = false): string {
  const t = (Math.abs(c) / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: decimales ? 2 : 0,
    maximumFractionDigits: decimales ? 2 : 0,
  });
  if (/^[0\s,]+$/.test(t)) return `${t} €`;
  return `${c < 0 ? '−' : '+'}${t} €`;
}

/** « 12 480 € », « −3 400 € ». */
export function eurosEntiers(c: number): string {
  const t = (Math.abs(c) / 100).toLocaleString('fr-FR', { maximumFractionDigits: 0 });
  return `${c < 0 && t !== '0' ? '−' : ''}${t} €`;
}

/** L'heure d'une photo, en heure de Paris : « lundi 14/09 à 08:12 ». */
export function heureDeParis(iso: string): string {
  const d = new Date(iso);
  const jour = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    weekday: 'long',
    day: '2-digit',
    month: '2-digit',
  }).format(d);
  const heure = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
  return `${jour} à ${heure}`;
}
